/**
 * Folder sidebar — the navigation pane.
 *
 * Renders the live folder tree of the connected account: the cache paints
 * first, the bridge refreshes behind it, and failures leave the cached
 * list on screen with the honest note (and a retry) underneath.
 */
import type { JSX } from "preact";

import { loadFolders, openFolder } from "../state/session.ts";
import {
  connection,
  folders,
  foldersLoading,
  folderError,
  activeFolderId,
  activeMessageId,
} from "../state/store.ts";

import { FolderIcon } from "./icons.tsx";

/**
 * Sidebar listing the active account's folders.
 *
 * @returns The sidebar section element.
 */
export function FolderSidebar(): JSX.Element {
  const state = connection.value;
  if (state === "no-account") {
    return (
      <div class="sidebar-empty" role="note">
        <FolderIcon />
        <p>Add an account to see your folders.</p>
      </div>
    );
  }
  if (state === "booting") {
    return (
      <div class="sidebar-empty" role="note">
        <FolderIcon />
        <p>Starting the local mail engine…</p>
      </div>
    );
  }
  if (folders.value.length === 0 && foldersLoading.value) {
    return (
      <div class="sidebar-empty" role="note">
        <FolderIcon />
        <p role="status">Loading folders…</p>
      </div>
    );
  }
  return (
    <>
      {folderError.value === null ? null : (
        <FolderError note={folderError.value} />
      )}
      <nav aria-label="Folders">
        <ul class="folder-list">
          {folders.value.map((folder) => (
            <li key={folder.id}>
              <button
                type="button"
                class="folder-row"
                aria-current={
                  activeFolderId.value === folder.id ? "page" : undefined
                }
                onClick={() => {
                  select(folder.id);
                }}
              >
                <FolderIcon />
                <span class="folder-name">{folder.name}</span>
                {folder.unread > 0 ? (
                  <span
                    class="folder-unread"
                    aria-label={`${folder.unread} unread`}
                  >
                    {folder.unread}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}

/**
 * The folder-load failure note with its retry affordance.
 *
 * @param props - The failure note.
 * @param props.note - User-facing sentence.
 * @returns The alert element.
 */
function FolderError(props: { readonly note: string }): JSX.Element {
  return (
    <div class="pane-alert" role="alert">
      <p>{props.note}</p>
      <button
        type="button"
        class="ghost-button"
        onClick={() => {
          void loadFolders();
        }}
      >
        Retry
      </button>
    </div>
  );
}

/**
 * Applies a folder selection; message selection resets with it. Selection
 * itself is local view state — the session fetch that follows can fail
 * without unselecting.
 *
 * @param id - Selected folder id.
 */
function select(id: string): void {
  activeFolderId.value = id;
  // Message list contents belong to a folder; dropping the selection avoids
  // a stale reader flashing the previous folder's message.
  activeMessageId.value = null;
  void openFolder(id);
}
